import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { STATUS_META } from "@/lib/constants";
import { fmtLocal } from "@/lib/dates";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge, LeadTypeBadge } from "@/components/Badges";
import DateRangeBar, { todayRange } from "@/components/DateRangeBar";

const METRIC_LABEL = { leads_assigned: "Leads assigned", opportunities: "Opportunities" };
const fmtStamp = (iso) => new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });

/** Drill-down behind one Reports number. `target` = {user_id, name, metric}. Defaults to today. */
export default function ReportDrillDialog({ target, onOpenChange }) {
  const [range, setRange] = useState(todayRange);
  useEffect(() => { if (target) setRange(todayRange()); }, [target]);
  const valid = range.start && range.end && range.start <= range.end;

  const q = useQuery({
    queryKey: ["reports", "drill", target?.user_id, target?.metric, range.start, range.end],
    queryFn: async () =>
      (await api.get("/reports/team/drill", { params: { user_id: target.user_id, metric: target.metric, start: range.start, end: range.end } })).data,
    enabled: Boolean(target) && Boolean(valid),
    placeholderData: keepPreviousData,
  });
  const items = q.data?.items || [];
  const metricLabel = METRIC_LABEL[target?.metric] || STATUS_META[target?.metric]?.label;

  return (
    <Dialog open={Boolean(target)} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[90vh] w-[calc(100vw-1.5rem)] overflow-y-auto rounded-2xl sm:max-w-3xl" data-testid="report-drill-dialog">
        <DialogHeader>
          <DialogTitle className="text-navy">
            {target?.name} · {metricLabel}
            <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600" data-testid="report-drill-count">{q.data ? items.length : "…"}</span>
          </DialogTitle>
        </DialogHeader>
        <DateRangeBar value={range} onChange={setRange} prefix="drill" />
        {q.isFetching && !q.data && <div className="py-6 text-center text-sm text-slate-400">Loading…</div>}
        {q.data && items.length === 0 && (
          <div className="py-8 text-center text-sm text-slate-400" data-testid="report-drill-empty">Nothing in this period.</div>
        )}
        {items.length > 0 && (
          <ul className="space-y-2 sm:hidden" data-testid="report-drill-cards">
            {items.map((r) => (
              <li key={r.opportunity_id || r.lead_id} className="rounded-xl border border-slate-100 p-3" data-testid={`report-drill-card-${r.lead_id}`}>
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/leads/${r.lead_id}`} className="font-semibold text-navy hover:underline">{r.name}</Link>
                  {r.status && <StatusBadge status={r.status} />}
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-500">
                  <a href={`tel:${r.phone}`} className="tabular-nums">{r.phone || "—"}</a>
                  <span>{q.data.date_label}: {fmtStamp(r.date)}</span>
                </div>
                {r.visit_date && <div className="mt-1 text-[11px] text-slate-500">Visit: {fmtLocal(r.visit_date)}</div>}
              </li>
            ))}
          </ul>
        )}
        {items.length > 0 && (
          <div className="hidden overflow-x-auto rounded-xl border border-slate-100 sm:block">
            <table className="w-full text-sm" data-testid="report-drill-table">
              <thead>
                <tr className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-400">
                  <th className="px-3 py-2">Lead</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">{q.data.date_label} date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.opportunity_id || r.lead_id} className="border-t border-slate-50 hover:bg-slate-50" data-testid={`report-drill-row-${r.lead_id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/leads/${r.lead_id}`} className="font-semibold text-navy hover:underline">{r.name}</Link>
                      <div className="mt-0.5"><LeadTypeBadge type={r.lead_type} /></div>
                    </td>
                    <td className="px-3 py-2 tabular-nums"><a href={`tel:${r.phone}`} className="hover:text-navy">{r.phone || "—"}</a></td>
                    <td className="px-3 py-2">
                      {r.status ? <StatusBadge status={r.status} /> : "—"}
                      {r.visit_date && <div className="mt-1 text-[11px] text-slate-500">Visit: {fmtLocal(r.visit_date)}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtStamp(r.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
